// src/pages/DebtStudio/components/Issues/DealStructure/index.jsx

import React, { useEffect, useCallback, useMemo, useState } from "react";
import Controls from "./SolutionParameters";
import StructureGrid from "./StructureGrid";
import SectionCard from "components/SectionCard";
import { computeTotalCOI } from "pages/DebtStudio/lib/costs";
import { buildSourcesUses } from "pages/DebtStudio/lib/buildSourcesUses";
import { buildWrapBaseByPrincipalPeriods } from "pages/DebtStudio/lib/buildWrapBase";
import { solveStructure } from "pages/DebtStudio/lib/structure";
import {
  useDebtStudio,
  validateDealForm,
} from "pages/DebtStudio/context/DebtStudioContext";
import { priceFromYield } from "lib/bondMath";
import dayjs from "lib/dayjs";
import { ChevronDown, ChevronUp } from "lucide-react";
import { trunc3 } from "pages/DebtStudio/utils/formatters";
import { enabledRefundingSolutionType } from "pages/DebtStudio/lib/refundingSolutionTypes";
import { deriveLdsPrincipalEndDates } from "./hooks/ldsSolver";
import { sumEnteredPrincipal } from "pages/DebtStudio/lib/principalAmount";
import {
  bucketCashflowsByPeriod,
  buildPriorDebtServiceCashflows,
  solveRefundingStructure,
} from "pages/DebtStudio/lib/refundingSavings";
import { getCombinationSolverTarget, isCombinationPurpose } from "pages/DebtStudio/lib/combination";

/* ---------- Utility: recompute price immediately ---------- */
function computeRowPrice(r, form) {
  const isLoan =
    String(form?.financingType || "").toLowerCase() === "loan";

  // Loans are always par instruments
  if (isLoan) return 100;

  if (!form?.deliveryDate || !r?.date) {
    const fallback = Number(r.price);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 100;
  }

  const couponPct = Number(r.coupon);
  const yieldPct = Number(r.yield);

  const couponRate = Number.isFinite(couponPct) ? couponPct / 100 : null;
  const yieldRate = Number.isFinite(yieldPct) ? yieldPct / 100 : null;

  if (!Number.isFinite(couponRate) || !Number.isFinite(yieldRate)) {
    const fallback = Number(r.price);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 100;
  }

  // premium → price to call, discount → price to maturity
  const pricingDate =
    r.call_date && couponRate > yieldRate ? r.call_date : r.date;

  const px = priceFromYield({
    couponRate,
    yieldRate,
    deliveryDate: form.deliveryDate,
    maturityDate: pricingDate,
    dayCount: form.dayCount,
    frequency: form.couponFrequency,
  });

  if (Number.isFinite(px) && px > 0) return trunc3(px);

  const fallback = Number(r.price);
  return Number.isFinite(fallback) && fallback > 0 ? fallback : 100;
}

/* ---------- Utility: safe number ---------- */
function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

/* ---------- Utility: compute total additional sources (fixed $ amounts) ---------- */
function computeTotalAdditionalSources(additionalSources) {
  const arr = Array.isArray(additionalSources) ? additionalSources : [];
  return arr.reduce((sum, it) => sum + num(it?.amount, 0), 0);
}

export default function DealStructure() {
  const {
    form,
    setForm,
    structureRows,
    setStructureRows,
    scale,
    setSolution,
    isLocked,
  } = useDebtStudio();

  const scheduleSummary = useMemo(() => {
    const rows = Array.isArray(structureRows) ? structureRows : [];
    const enteredRows = rows.filter((row) => {
      if (row?.principal === "" || row?.principal == null) return false;
      return Number.isFinite(Number(row.principal));
    });
    const dates = rows.map((row) => row?.date).filter(Boolean).sort();

    return {
      totalPrincipal: sumEnteredPrincipal(rows),
      enteredCount: enteredRows.length,
      finalMaturity: dates.length ? dates[dates.length - 1] : null,
    };
  }, [structureRows]);

  const [generatorOpen, setGeneratorOpen] = useState(false);

  const {
    structureMode,
    targetType,
    targetValue,
    amortizationType,
    dayCount,
    couponFrequency,
    principalFrequency,
    datedDate,
    deliveryDate,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    denomination,
    costOfIssuance,
  } = form;

  const isLoan =
    String(form?.financingType || "").toLowerCase() === "loan";

  /* ────────────────────────────────
     Solve handler
  ──────────────────────────────── */
  const handleSolve = useCallback(async () => {
    if (isLocked) return;

    const coiFn = (par) => computeTotalCOI(costOfIssuance || [], par);

    const totalAdditionalSources = computeTotalAdditionalSources(
      form?.additionalSources
    );

    const combination = isCombinationPurpose(form?.dealType);
    const effectiveTargetValue = combination
      ? getCombinationSolverTarget(form)
      : targetValue;

    const isWrapMode = structureMode === "wrap_lds";

    let baseDebtServiceByPeriod = [];

    if (isWrapMode) {
      const existingWrapCashflows = Array.isArray(form?.wrapManualCashflows)
        ? form.wrapManualCashflows
        : [];

      const principalEndDates = deriveLdsPrincipalEndDates({
        rows: structureRows,
        firstPrincipalDate,
        finalMaturityDate,
        principalFrequency,
      });

      const built = buildWrapBaseByPrincipalPeriods({
        principalEndDates,
        cashflows: existingWrapCashflows,
        startDate: deliveryDate || datedDate,
      });

      baseDebtServiceByPeriod = Array.isArray(built?.baseByBondYear)
        ? built.baseByBondYear
        : [];
    }

    const solverInput = {
      structureArray: structureRows,
      structureMode,
      targetType,
      targetValue: effectiveTargetValue,
      financingType: form?.financingType || "bond",
      amortizationType,
      amortizationPeriods: form?.amortizationPeriods,
      loanTermPeriods: form?.loanTermPeriods,
      costOfIssuance: coiFn,
      dayCount,
      couponFrequency,
      principalFrequency,
      datedDate,
      deliveryDate,
      firstCouponDate,
      firstPrincipalDate,
      finalMaturityDate,
      denomination,
      dealType: form?.dealType,
      capiEnabled: !!form?.capiEnabled,
      capiDate: form?.capiDate,
      capiInvestmentRate: form?.capiInvestmentRate ?? 0,
      additionalSources: totalAdditionalSources,
      baseDebtServiceByPeriod,
    };

    let rawSolution = solveStructure(solverInput);

    const refundedEntries = Array.isArray(form?.refunded_issues)
      ? form.refunded_issues.filter((entry) => entry?.entry_type === "ds_issue")
      : [];
    if (String(form?.dealType).toLowerCase() === "refunding" && refundedEntries.length) {
      const priorCashflows = buildPriorDebtServiceCashflows(refundedEntries, deliveryDate);
      const priorDebtService = bucketCashflowsByPeriod(
        priorCashflows,
        structureRows.map((row) => row.date),
        deliveryDate || datedDate
      );
      const shaped = await solveRefundingStructure({
        rows: structureRows,
        form,
        requiredPar: rawSolution?.parAmount,
        priorDebtService,
        solutionType: enabledRefundingSolutionType(form?.refundingSolutionType),
        fundingTarget: num(targetValue) + num(rawSolution?.capiUse),
        costOfIssuance: coiFn,
        additionalSources: totalAdditionalSources,
      });
      const grossProceeds = shaped.schedule.reduce(
        (sum, row) => sum + num(row.principal) * num(row.price, 100) / 100,
        0
      );
      const coi = coiFn(shaped.parAmount);
      rawSolution = {
        ...rawSolution,
        ...shaped,
        grossProceeds,
        coi,
        netProceeds: grossProceeds - coi,
      };
    }

    const solution =
      rawSolution &&
      typeof rawSolution === "object" &&
      !Array.isArray(rawSolution)
        ? {
            ...rawSolution,
            baseDebtServiceByPeriod,
          }
        : {
            schedule: Array.isArray(rawSolution) ? rawSolution : [],
            baseDebtServiceByPeriod,
          };

    let solvedRows = [];

    if (Array.isArray(solution)) {
      solvedRows = solution;
    } else if (Array.isArray(solution?.schedule)) {
      solvedRows = solution.schedule;
    } else if (Array.isArray(solution?.cashflows)) {
      solvedRows = solution.cashflows;
    }

    const updatedRows = (structureRows || []).map((r, i) => ({
      ...r,
      principal:
        solvedRows[i]?.principal != null
          ? Number(solvedRows[i].principal)
          : r.principal,
    }));

    const solutionWithBase = {
      ...solution,
      schedule: solvedRows,
      baseDebtServiceByPeriod,
    };

    if (String(form?.dealType).toLowerCase() === "refunding" && refundedEntries.length) {
      const { sources, uses } = buildSourcesUses(form, updatedRows);
      const sourceTotal = sources.reduce((sum, row) => sum + num(row.amount), 0);
      const useTotal = uses.reduce((sum, row) => sum + num(row.amount), 0);
      if (sourceTotal + 0.005 < useTotal) {
        throw new Error("The refunding solution does not fully fund the deposit. Adjust the structure or funding inputs and solve again.");
      }
    }

    if (combination) {
      const { sources, uses } = buildSourcesUses(form, updatedRows);
      const sourceTotal = sources.reduce((sum, row) => sum + num(row.amount), 0);
      const useTotal = uses.reduce((sum, row) => sum + num(row.amount), 0);
      if (sourceTotal + 0.005 < useTotal) {
        throw new Error(
          targetType === "par_amount"
            ? "The specified Total Principal Amount does not provide enough proceeds to fund the Refunding Requirement and transaction costs. Increase the principal amount or adjust the funding inputs."
            : "The Combination solution does not fully fund both the New Money Project Fund and Refunding Deposit. Adjust the structure or funding inputs and solve again."
        );
      }
    }

    setStructureRows(updatedRows);
    setSolution(solutionWithBase);

    setForm((f) => {
      const merged = {
        ...f,
        parAmount: solution?.parAmount ?? f.parAmount,
        grossProceeds: solution?.grossProceeds ?? f.grossProceeds,
        netProceeds: solution?.netProceeds ?? f.netProceeds,
        coi: solution?.coi ?? f.coi,
      };

      return {
        ...merged,
        sourcesUses: buildSourcesUses(merged, updatedRows, solution),
      };
    });

  }, [
    amortizationType,
    isLocked,
    costOfIssuance,
    couponFrequency,
    datedDate,
    dayCount,
    deliveryDate,
    denomination,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    form,
    principalFrequency,
    setForm,
    setSolution,
    setStructureRows,
    structureMode,
    structureRows,
    targetType,
    targetValue,
  ]);

  /* ────────────────────────────────
     Apply global coupon / interest rate changes
  ──────────────────────────────── */
  useEffect(() => {
    if (isLocked) return;
    if (!form?.couponRateToken) return;
    if (!Array.isArray(structureRows) || structureRows.length === 0) return;

    const nextRate =
      form?.couponRate === "" ||
      form?.couponRate == null ||
      Number.isNaN(Number(form.couponRate))
        ? null
        : Number(form.couponRate);

    const updated = structureRows.map((row) => ({
      ...row,
      coupon: nextRate,
      yield: isLoan ? nextRate : row?.yield,
      price: isLoan
        ? 100
        : computeRowPrice(
            {
              ...row,
              coupon: nextRate,
            },
            form
          ),
    }));

    setStructureRows(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.couponRateToken]);

  /* ────────────────────────────────
     Debug log
  ──────────────────────────────── */
  useEffect(() => {
    if (!Array.isArray(structureRows) || structureRows.length === 0) return;
  }, [structureRows]);

  /* ────────────────────────────────
     Recalculate price
  ──────────────────────────────── */
  useEffect(() => {
    if (isLocked) return;
    if (!Array.isArray(structureRows) || structureRows.length === 0) return;

    const updated = structureRows.map((r) => ({
      ...r,
      price: computeRowPrice(r, form),
    }));

    const pricesDiffer = structureRows.some(
      (r, i) => Number(r.price) !== Number(updated[i].price)
    );

    if (pricesDiffer) {
      setStructureRows(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLocked,
    form.deliveryDate,
    form.dayCount,
    form.couponFrequency,
    form.financingType,
  ]);

  /* Keep the Main amount aligned with all manual grid operations. */
  const handleBulkRowsChange = useCallback(
    (nextRows) => {
      const updatedRows = (Array.isArray(nextRows) ? nextRows : []).map(
        (row) => ({
          ...row,
          yield: isLoan ? row.coupon : row.yield,
          price: isLoan
            ? 100
            : computeRowPrice(
                {
                  ...row,
                  yield: isLoan ? row.coupon : row.yield,
                },
                form
              ),
        })
      );

      setStructureRows(updatedRows);
      setForm((current) => ({
        ...current,
        parAmount: sumEnteredPrincipal(updatedRows),
      }));
    },
    [form, isLoan, setForm, setStructureRows]
  );

  /* ────────────────────────────────
     Validation guard
  ──────────────────────────────── */
  const { ok } = validateDealForm(form);

  const formInvalid = !ok;
  const structureMissing =
    !structureRows || structureRows.length === 0;

  if (formInvalid || structureMissing) {
    const message = formInvalid
      ? "Please complete Issue Info and Dates & Terms"
      : "No structure has been created yet. Please return to the Deal Form tab to build the structure.";

    return <div className="text-red-500 text-sm p-4">{message}</div>;
  }

  const canSolvePrincipal =
    !isLocked &&
    Array.isArray(structureRows) &&
    structureRows.length > 0;

  const staleRefundingPrices =
    String(form?.dealType).toLowerCase() === "refunding" &&
    Array.isArray(scale) && scale.length > 0 &&
    structureRows.some((row) =>
      Number.isFinite(Number(row?.price)) &&
      Math.abs(Number(row.price) - computeRowPrice(row, form)) > 0.01
    );

  return (
    <div>
      <SectionCard
        title="Structure Details"
        subtitle="Enter or paste structure details directly in the table. Generate a principal solution with Principal Solver."
        className="mb-6"
      >
        <div className="mb-4 flex flex-wrap gap-x-8 gap-y-3 text-sm">
          <div>
            <div className="text-xs uppercase text-muted">Total Principal</div>
            <div className="font-semibold tabular-nums text-app">
              {scheduleSummary.totalPrincipal == null
                ? "—"
                : scheduleSummary.totalPrincipal.toLocaleString(undefined, {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  })}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted">Final Maturity</div>
            <div className="font-semibold tabular-nums text-app">
              {scheduleSummary.finalMaturity
                ? dayjs(scheduleSummary.finalMaturity).format("M/D/YYYY")
                : "—"}
            </div>
          </div>
          {!isLocked && (
            <div className="flex items-end">
              <button
                type="button"
                className="btn btn-secondary inline-flex items-center gap-2"
                onClick={() => setGeneratorOpen((open) => !open)}
                aria-expanded={generatorOpen}
                aria-controls="principal-schedule-generator"
              >
                {generatorOpen ? "Hide Solver" : "Principal Solver"}
                {generatorOpen ? (
                  <ChevronUp size={16} aria-hidden="true" />
                ) : (
                  <ChevronDown size={16} aria-hidden="true" />
                )}
              </button>
            </div>
          )}
        </div>

        {!isLocked && generatorOpen && (
          <div
            id="principal-schedule-generator"
            className="mb-5 rounded-lg border border-border bg-muted/20 p-4"
          >
            <div className="mb-3">
              <div className="text-sm font-semibold text-brand">
                Solve Principal Schedule
              </div>
              <div className="mt-0.5 text-xs text-muted">
                {scheduleSummary.enteredCount > 0
                  ? "Choose a method and target to replace the principal amounts in the grid."
                  : "Choose a method and target, or enter principal amounts manually in the grid below."}
              </div>
            </div>

            {staleRefundingPrices && (
              <div className="text-amber-700 text-sm mb-3" role="status">
                Bond prices may be stale for the current dates and terms. Review and reapply the ScaleBuilder scale before generating a schedule.
              </div>
            )}
            <Controls
              isLocked={false}
              onSolvePrincipal={handleSolve}
              canSolvePrincipal={canSolvePrincipal}
            />
          </div>
        )}

        <StructureGrid
          isLocked={!!isLocked}
          financingType={form?.financingType || "bond"}
          rows={structureRows}
        dayCount={dayCount}
        couponFrequency={couponFrequency}
        principalFrequency={principalFrequency}
        datedDate={datedDate}
        deliveryDate={deliveryDate}
        onPrincipalChange={(date, val) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date ? { ...r, principal: val } : r
            )
          )
        }
        onCouponChange={(date, pct) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date
                ? {
                    ...r,
                    coupon: pct,
                    yield: isLoan ? pct : r.yield,
                    price: isLoan
                      ? 100
                      : computeRowPrice(
                          {
                            ...r,
                            coupon: pct,
                            yield: isLoan ? pct : r.yield,
                          },
                          form
                        ),
                  }
                : r
            )
          )
        }
        onYieldChange={(date, pct) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date
                ? {
                    ...r,
                    yield: pct,
                    price: computeRowPrice({ ...r, yield: pct }, form),
                  }
                : r
            )
          )
        }
        onCallDateChange={(date, call) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date
                ? {
                    ...r,
                    call_date: call,
                    price: computeRowPrice({ ...r, call_date: call }, form),
                  }
                : r
            )
          )
        }
        onBondTypeChange={(date, t) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date ? { ...r, bond_type: t } : r
            )
          )
        }
        onTermGroupChange={(date, g) =>
          setStructureRows((rs) =>
            rs.map((r) =>
              r.date === date ? { ...r, term_group: g } : r
            )
          )
        }
          onBulkRowsChange={handleBulkRowsChange}
        />
      </SectionCard>
    </div>
  );
}
